/**
 * Reading a file of recorded outputs, so it can be previewed and checked against
 * the dataset before anything is sent.
 *
 * JSON: an array of records (or `{records: [...]}`). CSV: one row per record.
 * Either way a record names its item by `item_key` (also `key`) or `item_id`,
 * and carries `output`; `repetition`, `trace_id`, `cost_usd`, `latency_ms` and
 * `error` are optional. A CSV `output` cell holding JSON is parsed as JSON.
 */

import type { DatasetItem } from "@/features/datasets/types";
import { parseCsv } from "@/lib/csv";
import type { ImportRecord } from "../types";

export interface ParsedRecord {
  /** What the file used to name the item, shown in the preview. */
  ref: string;
  record: ImportRecord;
}

type Raw = Record<string, unknown>;

const text = (v: unknown) => (v === undefined || v === null ? "" : String(v).trim());
const num = (v: unknown) => (text(v) === "" || Number.isNaN(Number(v)) ? undefined : Number(v));

function jsonCell(cell: string): unknown {
  if (!/^[[{"]/.test(cell.trim())) return cell;
  try {
    return JSON.parse(cell);
  } catch {
    return cell;
  }
}

function toRecord(raw: Raw): ParsedRecord | null {
  const key = text(raw.item_key ?? raw.key);
  const id = text(raw.item_id);
  if (!key && !id) return null;
  const record: ImportRecord = { ...(key ? { item_key: key } : { item_id: id }), output: raw.output ?? null };
  const rep = num(raw.repetition);
  const cost = num(raw.cost_usd);
  const latency = num(raw.latency_ms);
  if (rep !== undefined) record.repetition = rep;
  if (text(raw.trace_id)) record.trace_id = text(raw.trace_id);
  if (cost !== undefined) record.cost_usd = cost;
  if (latency !== undefined) record.latency_ms = latency;
  if (text(raw.error)) record.error = text(raw.error);
  return { ref: key || id, record };
}

/** The file's records, or why it could not be read. */
export function parseRecordedFile(name: string, content: string): ParsedRecord[] | string {
  let rows: Raw[];
  if (name.toLowerCase().endsWith(".csv")) {
    const csv = parseCsv(content);
    if (!csv.header.includes("output")) return "The CSV needs an output column.";
    rows = csv.rows.map((r) =>
      Object.fromEntries(csv.header.map((h, i) => [h, h === "output" ? jsonCell(r[i] ?? "") : r[i]])),
    );
  } else {
    try {
      const value: unknown = JSON.parse(content);
      const list = Array.isArray(value) ? value : (value as { records?: unknown })?.records;
      if (!Array.isArray(list)) return "The JSON must be a list of records.";
      rows = list.filter((r): r is Raw => typeof r === "object" && r !== null);
    } catch {
      return "That file is not valid JSON.";
    }
  }
  const parsed = rows.map(toRecord);
  if (parsed.some((p) => p === null)) return "Every record needs an item_key (or item_id).";
  if (parsed.length === 0) return "The file has no records.";
  return parsed as ParsedRecord[];
}

/** Every name an item can be referred to by: its key (`metadata.key` or derived, as the API sends it) or id. */
export function itemRefs(items: DatasetItem[]): Set<string> {
  const refs = new Set<string>();
  for (const item of items) {
    if (item.key) refs.add(item.key);
    refs.add(item.id);
  }
  return refs;
}
